'use client';

import React from 'react';
import { motion } from 'framer-motion';

export type OrderStep = 'service' | 'upload' | 'review' | 'confirm';

const STEPS: { key: OrderStep; label: string }[] = [
  { key: 'service', label: 'Service' },
  { key: 'upload', label: 'Upload' },
  { key: 'review', label: 'Review' },
  { key: 'confirm', label: 'Confirm' },
];

export default function StepIndicator({ current }: { current: OrderStep }) {
  const activeIdx = STEPS.findIndex((s) => s.key === current);

  return (
    <div className="flex items-center">
      {STEPS.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-2">
              <motion.div
                initial={false}
                animate={{
                  scale: active ? 1.05 : 1,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${
                  done
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : active
                    ? 'bg-gradient-to-br from-purple-600 to-purple-500 border-purple-500 text-white shadow-lg shadow-purple-500/30'
                    : 'border-white/15 text-gray-500'
                }`}
              >
                {done ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
              </motion.div>
              <span
                className={`text-sm font-semibold hidden sm:inline ${
                  active || done ? 'text-white' : 'text-gray-500'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="h-px w-6 sm:w-10 mx-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ scaleX: i < activeIdx ? 1 : 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  style={{ originX: 0 }}
                  className="h-full bg-purple-500/60"
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
